package com.arena_web.ui.sections.news

import androidx.compose.runtime.*
import com.arena_web.data.dto.FootballCompetitionDto
import com.arena_web.data.dto.FootballMatchDto
import com.arena_web.data.dto.FootballStandingsDto
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.theme.LocalSportColors
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img

// =============================================================================
// Arena News — "Leagues & tournaments" mode. Day-strip calendar + competition
// chips + match list; standings open in a sheet/popup from the page level.
// =============================================================================

@Composable
fun NewsMatchesContentMobile(
    modeToggle: @Composable () -> Unit = {},
    competitions: List<FootballCompetitionDto>,
    matches: List<FootballMatchDto>,
    selectedDate: String,
    onDateSelected: (String) -> Unit,
    selectedCompetitions: Set<String>,
    onCompetitionsChanged: (Set<String>) -> Unit,
    onOpenStandings: (String) -> Unit,
    nextMatchday: String? = null,
    showStandings: Boolean = true,
    onMatchClick: (FootballMatchDto) -> Unit = {},
) {
    val t = LocalAppStrings.current
    var statusFilters by remember { mutableStateOf(emptySet<MatchStatusFilter>()) }
    val filtered = matches.filter { m -> statusFilters.isEmpty() || statusFilters.any { it.accepts(m) } }
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .flexGrow(1)
            .minHeight(0.px)
            .styleModifier { property("gap", "12px") }
    ) {
        // Title row: "Matches" opposite the news↔matches switcher (same
        // slot the feed gives "Today's news").
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            SectionHeader(t.newsMatchesTitle, mobile = true)
            modeToggle()
        }

        // Single header row: filters (left) · date switcher (centre) ·
        // standings (right).
        MatchesHeaderRow(
            selectedDate = selectedDate,
            onDateSelected = onDateSelected,
            competitions = competitions,
            selectedCompetitions = selectedCompetitions,
            onCompetitionsChanged = onCompetitionsChanged,
            statusFilters = statusFilters,
            onStatusChange = { statusFilters = it },
            onOpenStandings = onOpenStandings,
            showStandings = showStandings,
            mobile = true,
        )

        Div(
            attrs = {
                style {
                    property("width", "100%")
                    property("flex-grow", "1")
                    property("min-height", "0")
                    property("overflow-y", "auto")
                    property("overflow-x", "hidden")
                    property("scrollbar-width", "none")
                }
            }
        ) {
            Column(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", "12px") }) {
                if (filtered.isEmpty()) {
                    EmptyMatchesState(nextMatchday, onDateSelected, mobile = true)
                } else {
                    GroupedMatchList(filtered, competitions, mobile = true, onOpenStandings = onOpenStandings, onMatchClick = onMatchClick, showStandings = showStandings)
                }
                Div(
                    attrs = {
                        style {
                            property("width", "100%")
                            property("flex", "0 0 120px")
                            property("height", "120px")
                        }
                    }
                )
            }
        }
    }
}

@Composable
fun NewsMatchesContentDesktop(
    modeToggle: @Composable () -> Unit = {},
    competitions: List<FootballCompetitionDto>,
    matches: List<FootballMatchDto>,
    selectedDate: String,
    onDateSelected: (String) -> Unit,
    selectedCompetitions: Set<String>,
    onCompetitionsChanged: (Set<String>) -> Unit,
    onOpenStandings: (String) -> Unit,
    nextMatchday: String? = null,
    showStandings: Boolean = true,
    onMatchClick: (FootballMatchDto) -> Unit = {},
) {
    val t = LocalAppStrings.current
    var statusFilters by remember { mutableStateOf(emptySet<MatchStatusFilter>()) }
    val filtered = matches.filter { m -> statusFilters.isEmpty() || statusFilters.any { it.accepts(m) } }
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .flexGrow(1)
            .minHeight(0.px)
            .styleModifier { property("align-items", "center") }
    ) {
        Column(
            modifier = Modifier
                .fillMaxHeight()
                // Same column width as the feed mode — switching modes must
                // not make the content jump.
                .sWidth(1100)
                .styleModifier {
                    property("max-width", "100%")
                    property("gap", s(16))
                    property("padding-top", s(8))
                }
        ) {
            // Title row: "Matches" opposite the news↔matches switcher —
            // mirrors the feed mode's "Most discussed" row.
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                SectionHeader(t.newsMatchesTitle, mobile = false)
                modeToggle()
            }

            MatchesHeaderRow(
                selectedDate = selectedDate,
                onDateSelected = onDateSelected,
                competitions = competitions,
                selectedCompetitions = selectedCompetitions,
                onCompetitionsChanged = onCompetitionsChanged,
                statusFilters = statusFilters,
                onStatusChange = { statusFilters = it },
                onOpenStandings = onOpenStandings,
                showStandings = showStandings,
                mobile = false,
            )

            Div(
                attrs = {
                    style {
                        property("width", "100%")
                        property("flex-grow", "1")
                        property("min-height", "0")
                        property("overflow-y", "auto")
                        property("scrollbar-width", "none")
                    }
                }
            ) {
                Column(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", s(14)) }) {
                    if (filtered.isEmpty()) {
                        EmptyMatchesState(nextMatchday, onDateSelected, mobile = false)
                    } else {
                        GroupedMatchList(filtered, competitions, mobile = false, onOpenStandings = onOpenStandings, onMatchClick = onMatchClick, showStandings = showStandings)
                    }
                    Div(attrs = { style { property("width", "100%"); property("flex", "0 0 ${s(40)}"); property("height", s(40)) } })
                }
            }
        }
    }
}

// =============================================================================
// Pieces
// =============================================================================

private val WEEKDAY_SHORT = arrayOf("Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat")

/**
 * Compact date switcher: arrows around ONE centred two-line date. Top line is
 * a relative label (Today / Tomorrow / Yesterday, localized) or the weekday
 * for farther dates; bottom line is dd.MM. Arrows, a touch swipe, or a
 * horizontal trackpad wheel step one day.
 */
@Composable
private fun NewsDateStrip(selectedDate: String, onDateSelected: (String) -> Unit, mobile: Boolean) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current

    // Swipe bookkeeping — plain holders, no recomposition needed.
    val touchStartX = remember { doubleArrayOf(0.0) }
    val lastWheelAt = remember { doubleArrayOf(0.0) }

    val today = todayStr()
    val topLabel = when (selectedDate) {
        today -> t.newsToday
        addDaysStr(today, 1) -> t.newsTomorrow
        addDaysStr(today, -1) -> t.newsYesterday
        else -> WEEKDAY_SHORT[kotlin.js.Date(selectedDate).getDay()]
    }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("flex-shrink", "0")
                property("justify-content", "center")
                property("align-items", "center")
                property("gap", if (mobile) "8px" else s(10))
                // Horizontal swipes belong to the strip; vertical pans stay
                // with the page.
                property("touch-action", "pan-y")
            }
            .attrsModifier {
                onTouchStart { ev ->
                    touchStartX[0] = ev.changedTouches.item(0)?.clientX?.toDouble() ?: 0.0
                }
                onTouchEnd { ev ->
                    val endX = ev.changedTouches.item(0)?.clientX?.toDouble() ?: return@onTouchEnd
                    val delta = endX - touchStartX[0]
                    if (delta > 40) onDateSelected(addDaysStr(selectedDate, -1))
                    else if (delta < -40) onDateSelected(addDaysStr(selectedDate, 1))
                }
                onWheel { ev ->
                    // Horizontal trackpad scroll only — vertical wheel keeps
                    // scrolling the page. Throttled so one gesture = one step.
                    if (kotlin.math.abs(ev.deltaX) <= kotlin.math.abs(ev.deltaY)) return@onWheel
                    val now = kotlin.js.Date.now()
                    if (now - lastWheelAt[0] < 300) return@onWheel
                    lastWheelAt[0] = now
                    onDateSelected(addDaysStr(selectedDate, if (ev.deltaX > 0) 1 else -1))
                }
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        DateStripArrow(back = true, mobile = mobile) { onDateSelected(addDaysStr(selectedDate, -1)) }

        // Fixed-width centre block so the arrows don't shift when the label
        // length changes (e.g. "Aujourd'hui" vs "Sat").
        Column(
            modifier = Modifier
                .styleModifier {
                    property("width", if (mobile) "104px" else s(140))
                    property("flex-shrink", "0")
                    property("align-items", "center")
                    property("gap", "1px")
                    property("user-select", "none")
                },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            SpanText(
                topLabel,
                modifier = Modifier
                    .then(if (mobile) Modifier.mFontSize(16) else Modifier.sFontSize(21))
                    .fontWeight(600)
                    .color(sportColors.primary)
                    .styleModifier { property("white-space", "nowrap") }
            )
            SpanText(
                shortDate("${selectedDate}T00:00:00"),
                modifier = Modifier
                    .then(if (mobile) Modifier.mFontSize(13) else Modifier.sFontSize(16))
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
            )
        }

        DateStripArrow(back = false, mobile = mobile) { onDateSelected(addDaysStr(selectedDate, 1)) }
    }
}

@Composable
private fun DateStripArrow(back: Boolean, mobile: Boolean, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("flex-shrink", "0")
                property("width", if (mobile) "36px" else s(48))
                property("height", if (mobile) "36px" else s(48))
                property("border-radius", "100px")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
                property("user-select", "none")
            },
        contentAlignment = Alignment.Center
    ) {
        com.arena_web.ui.components.icons.Icon(
            src = com.arena_web.ui.theme.AppIcons.ChevronForward,
            modifier = Modifier
                .styleModifier {
                    property("width", if (mobile) "14px" else s(19))
                    property("height", if (mobile) "14px" else s(19))
                    property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V)
                    if (back) property("transform", "rotate(180deg)")
                }
        )
    }
}

/** Local today as yyyy-MM-dd. */
private fun todayStr(): String {
    val d = kotlin.js.Date()
    val m = (d.getMonth() + 1).toString().padStart(2, '0')
    val day = d.getDate().toString().padStart(2, '0')
    return "${d.getFullYear()}-$m-$day"
}

/** yyyy-MM-dd shifted by [offset] days. */
private fun addDaysStr(date: String, offset: Int): String {
    val parts = date.split("-").map { it.toInt() }
    val d = kotlin.js.Date(parts[0], parts[1] - 1, parts[2] + offset)
    val m = (d.getMonth() + 1).toString().padStart(2, '0')
    val day = d.getDate().toString().padStart(2, '0')
    return "${d.getFullYear()}-$m-$day"
}

/**
 * Single header row: filters button (left, opens the league + status
 * dropdown) · date switcher (centre) · standings shortcut (right).
 */
@Composable
private fun MatchesHeaderRow(
    selectedDate: String,
    onDateSelected: (String) -> Unit,
    competitions: List<FootballCompetitionDto>,
    selectedCompetitions: Set<String>,
    onCompetitionsChanged: (Set<String>) -> Unit,
    statusFilters: Set<MatchStatusFilter>,
    onStatusChange: (Set<MatchStatusFilter>) -> Unit,
    onOpenStandings: (String) -> Unit,
    showStandings: Boolean,
    mobile: Boolean,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("flex-shrink", "0")
                property("align-items", "center")
                property("gap", if (mobile) "8px" else s(10))
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        FiltersButton(
            competitions = competitions,
            selectedCompetitions = selectedCompetitions,
            onCompetitionsChanged = onCompetitionsChanged,
            statusFilters = statusFilters,
            onStatusChange = onStatusChange,
            mobile = mobile,
        )

        Box(modifier = Modifier.styleModifier { property("flex", "1 1 0"); property("min-width", "0") }) {
            NewsDateStrip(selectedDate, onDateSelected, mobile)
        }

        // Standings — the first checked league's table, or the top
        // competition's when no league filter is active. Hidden for sports
        // without tables (tennis tours).
        if (showStandings) {
            val standingsCode =
                competitions.firstOrNull { it.code in selectedCompetitions }?.code
                    ?: competitions.firstOrNull()?.code
            HeaderRoundButton(
                icon = com.arena_web.ui.theme.AppIcons.BarChart,
                mobile = mobile,
                highlight = false,
                onClick = { standingsCode?.let(onOpenStandings) },
            )
        }
    }
}

/** Round filter trigger + the filters sheet (statuses first, then leagues &
 *  tournaments with logos and checkboxes). */
@Composable
private fun FiltersButton(
    competitions: List<FootballCompetitionDto>,
    selectedCompetitions: Set<String>,
    onCompetitionsChanged: (Set<String>) -> Unit,
    statusFilters: Set<MatchStatusFilter>,
    onStatusChange: (Set<MatchStatusFilter>) -> Unit,
    mobile: Boolean,
) {
    var showSheet by remember { mutableStateOf(false) }
    val filtersActive = selectedCompetitions.isNotEmpty() || statusFilters.isNotEmpty()

    HeaderRoundButton(
        icon = com.arena_web.ui.theme.AppIcons.Filter,
        mobile = mobile,
        highlight = filtersActive,
        onClick = { showSheet = true },
    )

    if (showSheet) {
        val content: @Composable () -> Unit = {
            // Scroll container: with many competitions the league grid grows
            // past the sheet/popup height — cap and scroll instead of clipping.
            Column(
                modifier = Modifier.fillMaxWidth().styleModifier {
                    property("max-height", if (mobile) "70vh" else "72vh")
                    property("overflow-y", "auto")
                    property("scrollbar-width", "none")
                    property("-ms-overflow-style", "none")
                }
            ) {
                FiltersSheetContent(
                    competitions = competitions,
                    selectedCompetitions = selectedCompetitions,
                    onCompetitionsChanged = onCompetitionsChanged,
                    statusFilters = statusFilters,
                    onStatusChange = onStatusChange,
                    mobile = mobile,
                )
            }
        }
        if (mobile) {
            com.arena_web.ui.components.overlay.BottomSheet(onDismiss = { showSheet = false }) { content() }
        } else {
            com.arena_web.ui.components.overlay.DesktopPopup(onDismiss = { showSheet = false }, widthPx = 520) { content() }
        }
    }
}

@Composable
private fun FiltersSheetContent(
    competitions: List<FootballCompetitionDto>,
    selectedCompetitions: Set<String>,
    onCompetitionsChanged: (Set<String>) -> Unit,
    statusFilters: Set<MatchStatusFilter>,
    onStatusChange: (Set<MatchStatusFilter>) -> Unit,
    mobile: Boolean,
) {
    val t = LocalAppStrings.current
    fun statusLabel(f: MatchStatusFilter) = when (f) {
        MatchStatusFilter.Live -> "LIVE"
        MatchStatusFilter.Finished -> t.newsFilterFinished
        MatchStatusFilter.Upcoming -> t.newsFilterUpcoming
    }
    Column(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", "2px") }) {
        FilterSectionTitle(t.newsFilterStatusTitle, mobile, first = true)
        // Status — one row of three tiles. Raw Div: Kobweb's Box is itself a
        // CSS grid that stacks children, so real multi-cell grids need a Div.
        Div(
            attrs = {
                style {
                    property("display", "grid")
                    property("grid-template-columns", "repeat(3, 1fr)")
                    property("gap", "8px")
                    property("width", "100%")
                }
            }
        ) {
            MatchStatusFilter.entries.forEach { filter ->
                FilterTile(
                    label = statusLabel(filter),
                    emblem = null,
                    statusIcon = when (filter) {
                        MatchStatusFilter.Live -> com.arena_web.ui.theme.AppIcons.Pulse
                        MatchStatusFilter.Finished -> com.arena_web.ui.theme.AppIcons.CheckMark
                        MatchStatusFilter.Upcoming -> com.arena_web.ui.theme.AppIcons.Time
                    },
                    // LIVE reads red, finished green (web-wide status colours).
                    statusIconFilter = when (filter) {
                        MatchStatusFilter.Live -> AppColors.IconDestructiveFilter
                        MatchStatusFilter.Finished -> AppColors.IconSuccessFilter
                        MatchStatusFilter.Upcoming -> null
                    },
                    checked = filter in statusFilters,
                    mobile = mobile,
                    onToggle = {
                        onStatusChange(if (filter in statusFilters) statusFilters - filter else statusFilters + filter)
                    },
                )
            }
        }

        FilterSectionTitle(t.newsFilterLeaguesTitle, mobile, first = false)
        // Leagues — two-column grid of logo tiles.
        Div(
            attrs = {
                style {
                    property("display", "grid")
                    property("grid-template-columns", "repeat(2, 1fr)")
                    property("gap", "8px")
                    property("width", "100%")
                }
            }
        ) {
            competitions.forEach { competition ->
                FilterTile(
                    label = competition.name,
                    emblem = competition.emblem.takeIf { it.isNotBlank() },
                    emblemCode = competition.code,
                    // Competitions without an emblem (e.g. Conference League)
                    // fall back to the bundled trophy glyph.
                    statusIcon = if (competition.emblem.isBlank()) com.arena_web.ui.theme.AppIcons.Trophy else null,
                    checked = competition.code in selectedCompetitions,
                    mobile = mobile,
                    onToggle = {
                        onCompetitionsChanged(
                            if (competition.code in selectedCompetitions) selectedCompetitions - competition.code
                            else selectedCompetitions + competition.code
                        )
                    },
                )
            }
        }
    }
}

@Composable
private fun FilterSectionTitle(text: String, mobile: Boolean, first: Boolean) {
    SpanText(
        text,
        modifier = Modifier
            .then(if (mobile) Modifier.mFontSize(15) else Modifier.sFontSize(16))
            .fontWeight(600)
            .color(com.arena_web.ui.theme.ThemeVars.Text1)
            .styleModifier {
                property("padding", if (first) "0 0 6px 0" else "16px 0 6px 0")
            }
    )
}

/** One grid tile: optional league logo + label; selection = sport-primary
 *  border + tinted fill with a small tick in the corner. */
@Composable
private fun FilterTile(
    label: String,
    emblem: String?,
    checked: Boolean,
    mobile: Boolean,
    onToggle: () -> Unit,
    emblemCode: String? = null,
    /** Project icon (status tiles) — rendered where a league emblem would be. */
    statusIcon: String? = null,
    /** Colour filter for [statusIcon] (red LIVE, green finished). */
    statusIconFilter: String? = null,
) {
    val sportColors = LocalSportColors.current
    Div(
        attrs = {
            style {
                property("position", "relative")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("gap", "8px")
                property("min-height", if (mobile) "48px" else "52px")
                property("padding", "8px 10px")
                property("border-radius", "14px")
                property("cursor", "pointer")
                property("box-sizing", "border-box")
                property("transition", "background 0.15s ease, border-color 0.15s ease")
                if (checked) {
                    property("border", "1px solid ${sportColors.primary}")
                    property("background", "${sportColors.primary}1F")
                } else {
                    property("border", "1px solid var(--l-tile-border, rgba(180, 204, 246, 0.15))")
                    property("background", "var(--l-tile, rgba(9, 20, 45, 0.6))")
                }
            }
            onClick { onToggle() }
        }
    ) {
        if (statusIcon != null) {
            Icon(
                src = statusIcon,
                modifier = Modifier.styleModifier {
                    property("width", if (mobile) "16px" else "18px")
                    property("height", if (mobile) "16px" else "18px")
                    property("flex-shrink", "0")
                    property("filter", statusIconFilter ?: com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V)
                }
            )
        }
        if (emblem != null) {
            Img(
                src = emblem,
                alt = "",
                attrs = Modifier
                    .styleModifier {
                        property("width", if (mobile) "22px" else "24px")
                        property("height", if (mobile) "22px" else "24px")
                        property("object-fit", "contain")
                        property("flex-shrink", "0")
                        leagueEmblemFilter(emblemCode)?.let { property("filter", it) }
                    }
                    .toAttrs()
            )
        }
        SpanText(
            label,
            modifier = Modifier
                .then(if (mobile) Modifier.mFontSize(13) else Modifier.sFontSize(14))
                .fontWeight(500)
                .color(if (checked) org.jetbrains.compose.web.css.Color("var(--arena-text-1)") else com.arena_web.ui.theme.ThemeVars.Text2)
                .styleModifier {
                    property("min-width", "0")
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                    property("white-space", "nowrap")
                }
        )
        // Corner tick for the selected state.
        if (checked) {
            Div(attrs = {
                style {
                    property("position", "absolute")
                    property("top", "-5px")
                    property("right", "-5px")
                    property("width", "16px")
                    property("height", "16px")
                    property("border-radius", "50%")
                    property("background", sportColors.primary.toString())
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                }
            }) {
                com.arena_web.ui.components.icons.Icon(
                    src = com.arena_web.ui.theme.AppIcons.Check,
                    modifier = Modifier.styleModifier {
                        property("width", "9px")
                        property("height", "9px")
                        property("filter", sportColors.iconFilterOnPrimary)
                    }
                )
            }
        }
    }
}

/** Round 36/44px icon button in the top-bar chrome. [highlight] paints a
 *  sport-primary dot marking a non-default filter set. */
@Composable
private fun HeaderRoundButton(icon: String, mobile: Boolean, highlight: Boolean, onClick: () -> Unit) {
    val sportColors = LocalSportColors.current
    val size = if (mobile) 36 else 44
    Box(modifier = Modifier.position(org.jetbrains.compose.web.css.Position.Relative).styleModifier { property("flex-shrink", "0") }) {
        Box(
            modifier = Modifier
                .cursor(Cursor.Pointer)
                .onClick { onClick() }
                .styleModifier {
                    property("width", "${size}px")
                    property("height", "${size}px")
                    property("border-radius", "100px")
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                    property("border", "0.3px solid transparent")
                    property(
                        "background",
                        "${AppGradients.InteractiveSecondary} padding-box, " +
                            "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                            "${AppGradients.ButtonBorderGradientPrimary} border-box"
                    )
                },
            contentAlignment = Alignment.Center
        ) {
            com.arena_web.ui.components.icons.Icon(
                src = icon,
                modifier = Modifier.styleModifier {
                    property("width", "${size * 16 / 36}px")
                    property("height", "${size * 16 / 36}px")
                    property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V)
                }
            )
        }
        if (highlight) {
            Div(attrs = {
                style {
                    property("position", "absolute")
                    property("top", "0")
                    property("right", "0")
                    property("width", "10px")
                    property("height", "10px")
                    property("border-radius", "50%")
                    property("background", sportColors.primary.toString())
                }
            })
        }
    }
}

/** Client-side status categories — multi-select via the filters sheet;
 *  an empty selection means "show everything". */
enum class MatchStatusFilter {
    Live, Finished, Upcoming;

    fun accepts(match: FootballMatchDto): Boolean = when (this) {
        Live -> match.status == "IN_PLAY" || match.status == "PAUSED"
        Finished -> match.status == "FINISHED" || match.status == "AWARDED"
        Upcoming -> match.status == "TIMED" || match.status == "SCHEDULED"
    }
}

/**
 * football24-style calendar body: matches grouped under tournament headers
 * (emblem + name + standings link), compact score rows inside one panel per
 * competition.
 */
@Composable
private fun GroupedMatchList(
    matches: List<FootballMatchDto>,
    competitions: List<FootballCompetitionDto>,
    mobile: Boolean,
    onOpenStandings: (String) -> Unit,
    onMatchClick: (FootballMatchDto) -> Unit = {},
    showStandings: Boolean = true,
) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    // Preserve the competitions' importance order; unknown codes go last.
    val order = competitions.mapIndexed { i, c -> c.code to i }.toMap()
    val groups = matches.groupBy { it.competitionCode }.entries.sortedBy { order[it.key] ?: Int.MAX_VALUE }
    val emblems = competitions.associate { it.code to it.emblem }

    groups.forEach { (code, groupMatches) ->
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .then(if (mobile) Modifier.mBorderRadius(20) else Modifier.sBorderRadius(22))
                .overflow(com.varabyte.kobweb.compose.css.Overflow.Hidden)
                .styleModifier {
                    property("flex-shrink", "0")
                    property("border", "0.5px solid ${AppGradients.NewsCardBorder}")
                    property("background", "var(--l-surface-2, var(--l-s2, #09142D))")
                }
        ) {
            // Tournament header — emblem + name, standings shortcut at right.
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .then(if (mobile) Modifier.mPadding(10, 14) else Modifier.sPadding(14, 20))
                    .styleModifier { property("background", "rgba(60, 121, 241, 0.06)") },
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(
                    modifier = Modifier.then(if (mobile) Modifier.mGap(8) else Modifier.sGap(10)),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    val emblem = emblems[code].orEmpty()
                    if (emblem.isNotBlank()) {
                        Img(
                            src = emblem,
                            alt = "",
                            attrs = Modifier
                                .then(if (mobile) Modifier.mSize(18) else Modifier.sSize(24))
                                .styleModifier {
                                    property("object-fit", "contain")
                                    property("flex-shrink", "0")
                                    leagueEmblemFilter(code)?.let { property("filter", it) }
                                }
                                .toAttrs()
                        )
                    }
                    SpanText(
                        groupMatches.first().competitionName,
                        modifier = Modifier
                            .then(if (mobile) Modifier.mFontSize(13) else Modifier.sFontSize(17))
                            .fontWeight(600)
                            .color(com.arena_web.ui.theme.ThemeVars.Text1)
                    )
                }
                if (showStandings) {
                    SpanText(
                        "${t.newsStandingsTitle} →",
                        modifier = Modifier
                            .then(if (mobile) Modifier.mFontSize(12) else Modifier.sFontSize(14))
                            .fontWeight(500)
                            .cursor(Cursor.Pointer)
                            .onClick { onOpenStandings(code) }
                            .styleModifier {
                                property("color", sportColors.primary.toString())
                                property("white-space", "nowrap")
                            }
                    )
                }
            }

            groupMatches.forEachIndexed { index, match ->
                if (index > 0) {
                    Div(attrs = {
                        style {
                            property("width", "100%")
                            property("height", "1px")
                            property("background", "rgba(180, 204, 246, 0.08)")
                        }
                    })
                }
                MatchRow(match, mobile, onClick = { onMatchClick(match) })
            }
        }
    }
}

/** Compact score row: status/time · home · score · away. */
@Composable
internal fun MatchRow(
    match: FootballMatchDto,
    mobile: Boolean,
    onClick: () -> Unit = {},
) {
    val sportColors = LocalSportColors.current
    val live = match.status == "IN_PLAY" || match.status == "PAUSED"
    val statusLabel = when {
        match.status == "FINISHED" -> "FT"
        live -> "LIVE"
        match.status == "TIMED" || match.status == "SCHEDULED" -> kickoffTime(match.utcDate)
        else -> "—"
    }
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .then(if (mobile) Modifier.mPadding(10, 14) else Modifier.sPadding(16, 22)),
        verticalAlignment = Alignment.CenterVertically
    ) {
        SpanText(
            statusLabel,
            modifier = Modifier
                .then(if (mobile) Modifier.mFontSize(11) else Modifier.sFontSize(14))
                .fontWeight(if (live) 700 else 400)
                .styleModifier {
                    property("flex", "0 0 ${if (mobile) "44px" else s(64)}")
                    property("color", if (live) "#FF4242" else "var(--arena-text-3)")
                    property("white-space", "nowrap")
                }
        )
        TeamCell(match.homeTeam, match.homeCrest, mobile, alignEnd = false)
        SpanText(
            if (match.homeScore != null && match.awayScore != null) "${match.homeScore} : ${match.awayScore}" else "– : –",
            modifier = Modifier
                .then(if (mobile) Modifier.mFontSize(14).mPadding(0, 8) else Modifier.sFontSize(22).sPadding(0, 18))
                .fontWeight(700)
                .styleModifier {
                    property("flex-shrink", "0")
                    property("color", if (live) sportColors.primary.toString() else "var(--arena-text-1)")
                    property("white-space", "nowrap")
                }
        )
        TeamCell(match.awayTeam, match.awayCrest, mobile, alignEnd = true)
    }
}

@Composable
private fun TeamCell(name: String, crest: String, mobile: Boolean, alignEnd: Boolean) {
    Row(
        modifier = Modifier
            .then(if (mobile) Modifier.mGap(8) else Modifier.sGap(12))
            .styleModifier {
                property("flex", "1 1 0")
                property("min-width", "0")
                if (alignEnd) property("justify-content", "flex-end")
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        if (!alignEnd && crest.isNotBlank()) TeamCrest(crest, mobile)
        SpanText(
            name,
            modifier = Modifier
                .then(if (mobile) Modifier.mFontSize(13) else Modifier.sFontSize(18))
                .fontWeight(500)
                .color(com.arena_web.ui.theme.ThemeVars.Text1)
                .styleModifier {
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                    property("white-space", "nowrap")
                    if (alignEnd) property("text-align", "right")
                }
        )
        if (alignEnd && crest.isNotBlank()) TeamCrest(crest, mobile)
    }
}

@Composable
private fun TeamCrest(crest: String, mobile: Boolean) {
    Img(
        src = crest,
        alt = "",
        attrs = Modifier
            .then(if (mobile) Modifier.mSize(22) else Modifier.sSize(36))
            .styleModifier {
                property("object-fit", "contain")
                property("flex-shrink", "0")
            }
            .toAttrs()
    )
}

// =============================================================================
// Standings table (rendered inside a BottomSheet on phones / DesktopPopup on
// desktop — the host page owns the overlay).
// =============================================================================

// Qualification-zone tints — football24-style row highlighting + legend.
private const val ZONE_CL = "#2ECC71"
private const val ZONE_EL = "#F5A623"
private const val ZONE_RELEGATION = "#FF4242"

/** Zone colour for a table row, or null for the neutral mid-table. */
private fun zoneColor(competitionCode: String, position: Int, totalRows: Int): String? = when {
    // CL league phase: top 8 go straight to the round of 16.
    competitionCode == "CL" -> ZONE_CL.takeIf { position <= 8 }
    position <= 4 -> ZONE_CL
    position <= 6 -> ZONE_EL
    position > totalRows - 3 -> ZONE_RELEGATION
    else -> null
}

@Composable
fun StandingsTable(
    standings: FootballStandingsDto,
    mobile: Boolean,
    /** false → the caller renders its own (e.g. sticky) title row. */
    showTitle: Boolean = true,
) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    val code = standings.competition.code
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .then(if (mobile) Modifier.mGap(4) else Modifier.sGap(6))
    ) {
        if (showTitle) Row(
            modifier = Modifier.then(if (mobile) Modifier.mGap(8) else Modifier.sGap(10))
                .styleModifier { property("margin-bottom", if (mobile) "8px" else s(10)); property("align-items", "center") },
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (standings.competition.emblem.isNotBlank()) {
                Img(
                    src = standings.competition.emblem,
                    alt = "",
                    attrs = Modifier
                        .then(if (mobile) Modifier.mSize(24) else Modifier.sSize(28))
                        .styleModifier {
                            property("object-fit", "contain")
                            property("flex-shrink", "0")
                            leagueEmblemFilter(standings.competition.code)?.let { property("filter", it) }
                        }
                        .toAttrs()
                )
            }
            SpanText(
                standings.competition.name,
                modifier = Modifier
                    .then(if (mobile) Modifier.mFontSize(18) else Modifier.sFontSize(20))
                    .fontWeight(600)
                    .color(com.arena_web.ui.theme.ThemeVars.Text1)
            )
        }
        // Header row.
        StandingsRowShell(mobile, zone = null) {
            StandingsCell("#", flex = null, mobile = mobile, subtle = true, width = if (mobile) 22 else 30)
            StandingsCell("", flex = 1, mobile = mobile, subtle = true)
            StandingsCell(t.newsStPlayed, flex = null, mobile = mobile, subtle = true)
            StandingsCell(t.newsStWin, flex = null, mobile = mobile, subtle = true)
            StandingsCell(t.newsStDraw, flex = null, mobile = mobile, subtle = true)
            StandingsCell(t.newsStLoss, flex = null, mobile = mobile, subtle = true)
            StandingsCell(t.newsStGoals, flex = null, mobile = mobile, subtle = true, width = if (mobile) 44 else 60)
            StandingsCell(t.newsStPts, flex = null, mobile = mobile, subtle = true, bold = true)
            if (!mobile) StandingsCell("", flex = null, mobile = false, subtle = true, width = 84)
        }
        standings.rows.forEach { row ->
            StandingsRowShell(mobile, zone = zoneColor(code, row.position, standings.rows.size)) {
                StandingsCell(row.position.toString(), flex = null, mobile = mobile, subtle = true, width = if (mobile) 22 else 30)
                Row(
                    modifier = Modifier
                        .styleModifier {
                            property("flex", "1 1 0")
                            property("min-width", "0")
                            property("align-items", "center")
                            property("gap", if (mobile) "8px" else s(8))
                        },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    if (row.crest.isNotBlank()) {
                        Img(
                            src = row.crest,
                            alt = "",
                            attrs = Modifier
                                .then(if (mobile) Modifier.mSize(18) else Modifier.sSize(26))
                                .styleModifier { property("object-fit", "contain"); property("flex-shrink", "0") }
                                .toAttrs()
                        )
                    }
                    SpanText(
                        row.team,
                        modifier = Modifier
                            .then(if (mobile) Modifier.mFontSize(13) else Modifier.sFontSize(16))
                            .color(com.arena_web.ui.theme.ThemeVars.Text1)
                            .styleModifier {
                                property("overflow", "hidden")
                                property("text-overflow", "ellipsis")
                                property("white-space", "nowrap")
                            }
                    )
                }
                StandingsCell(row.played.toString(), flex = null, mobile = mobile)
                StandingsCell(row.won.toString(), flex = null, mobile = mobile)
                StandingsCell(row.draw.toString(), flex = null, mobile = mobile)
                StandingsCell(row.lost.toString(), flex = null, mobile = mobile)
                StandingsCell("${row.goalsFor}:${row.goalsAgainst}", flex = null, mobile = mobile, width = if (mobile) 44 else 60)
                SpanText(
                    row.points.toString(),
                    modifier = Modifier
                        .then(if (mobile) Modifier.mFontSize(13) else Modifier.sFontSize(16))
                        .fontWeight(700)
                        .styleModifier {
                            property("color", sportColors.primary.toString())
                            property("flex", "0 0 ${if (mobile) "30px" else s(40)}")
                            property("text-align", "center")
                        }
                )
                // Form dots — desktop only (the phone table is already tight).
                if (!mobile) FormDots(row.form)
            }
        }

        // Zone legend (domestic leagues; the CL table's green = straight to
        // the round of 16, which the CL zone label already conveys).
        Row(
            modifier = Modifier
                .styleModifier {
                    property("margin-top", if (mobile) "10px" else s(12))
                    property("gap", if (mobile) "14px" else s(18))
                    property("align-items", "center")
                    property("flex-wrap", "wrap")
                },
            verticalAlignment = Alignment.CenterVertically
        ) {
            LegendItem(ZONE_CL, t.newsZoneCl, mobile)
            if (code != "CL") {
                LegendItem(ZONE_EL, t.newsZoneEl, mobile)
                LegendItem(ZONE_RELEGATION, t.newsZoneRelegation, mobile)
            }
        }
    }
}

/** Coloured dot + label for the zone legend. */
@Composable
private fun LegendItem(color: String, label: String, mobile: Boolean) {
    Row(
        modifier = Modifier.styleModifier { property("gap", "6px"); property("align-items", "center") },
        verticalAlignment = Alignment.CenterVertically
    ) {
        Div(attrs = {
            style {
                property("width", "8px")
                property("height", "8px")
                property("border-radius", "50%")
                property("background", color)
                property("flex-shrink", "0")
            }
        })
        SpanText(
            label,
            modifier = Modifier
                .then(if (mobile) Modifier.mFontSize(11) else Modifier.sFontSize(13))
                .color(com.arena_web.ui.theme.ThemeVars.Text3)
        )
    }
}

/** Last-5 form as coloured dots: green W · gray D · red L. */
@Composable
private fun FormDots(form: String?) {
    Row(
        modifier = Modifier.styleModifier {
            property("flex", "0 0 ${s(84)}")
            property("gap", "4px")
            property("justify-content", "flex-end")
            property("align-items", "center")
        },
        verticalAlignment = Alignment.CenterVertically
    ) {
        form.orEmpty().split(",").map { it.trim() }.filter { it.isNotEmpty() }.takeLast(5).forEach { r ->
            Div(attrs = {
                style {
                    property("width", "10px")
                    property("height", "10px")
                    property("border-radius", "50%")
                    property(
                        "background",
                        when (r.uppercase()) {
                            "W" -> ZONE_CL
                            "L" -> ZONE_RELEGATION
                            else -> "rgba(180, 204, 246, 0.35)"
                        }
                    )
                }
            })
        }
    }
}

@Composable
private fun StandingsRowShell(mobile: Boolean, zone: String?, content: @Composable () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .then(if (mobile) Modifier.mPadding(6, 0) else Modifier.sPadding(10, 0))
            .styleModifier {
                property("align-items", "center")
                // Qualification-zone tint: faint fill + a 2px colour bar.
                if (zone != null) {
                    property("background", "${zone}14")
                    property("box-shadow", "inset 2px 0 0 $zone")
                }
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        content()
    }
}

@Composable
private fun StandingsCell(
    text: String,
    flex: Int?,
    mobile: Boolean,
    subtle: Boolean = false,
    bold: Boolean = false,
    width: Int? = null,
) {
    SpanText(
        text,
        modifier = Modifier
            .then(if (mobile) Modifier.mFontSize(if (subtle) 11 else 13) else Modifier.sFontSize(if (subtle) 13 else 16))
            .fontWeight(if (bold) 700 else 400)
            .color(if (subtle) org.jetbrains.compose.web.css.Color("var(--arena-text-3)") else com.arena_web.ui.theme.ThemeVars.Text1)
            .styleModifier {
                if (flex != null) {
                    property("flex", "$flex 1 0")
                    property("min-width", "0")
                } else {
                    val w = width ?: (if (mobile) 30 else 40)
                    property("flex", "0 0 ${if (mobile) "${w}px" else s(w)}")
                    property("text-align", "center")
                }
            }
    )
}

/** Empty-day state: message + a one-tap jump to the nearest matchday when
 *  the backend found one in the next ten days. */
@Composable
private fun EmptyMatchesState(nextMatchday: String?, onDateSelected: (String) -> Unit, mobile: Boolean) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .minHeight(if (mobile) 160.px else 200.px)
            .then(if (mobile) Modifier.mPadding(24, 0).mGap(14) else Modifier.sPadding(24, 0).sGap(16))
            .styleModifier {
                property("align-items", "center")
                property("justify-content", "center")
            },
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        SpanText(
            t.newsNoMatches,
            modifier = Modifier
                .then(if (mobile) Modifier.mFontSize(14) else Modifier.sFontSize(16))
                .color(com.arena_web.ui.theme.ThemeVars.Text2)
        )
        if (nextMatchday != null) {
            val jsDate = kotlin.js.Date(nextMatchday)
            val label = "${t.newsNextMatches}: ${WEEKDAY_SHORT[jsDate.getDay()]} ${shortDate("${nextMatchday}T00:00:00")} →"
            Box(
                modifier = Modifier
                    .cursor(Cursor.Pointer)
                    .onClick { onDateSelected(nextMatchday) }
                    .then(if (mobile) Modifier.mBorderRadius(100).mPadding(10, 18) else Modifier.sBorderRadius(100).sPadding(10, 20))
                    .styleModifier {
                        property("border", "1px solid ${sportColors.primary}")
                        property("background", "${sportColors.primary}1F")
                    },
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    label,
                    modifier = Modifier
                        .then(if (mobile) Modifier.mFontSize(13) else Modifier.sFontSize(14))
                        .fontWeight(600)
                        .styleModifier { property("color", sportColors.primary.toString()) }
                )
            }
        }
    }
}

/** Emblems whose brand colours are dark navy/purple and sink into the dark
 *  theme (checked visually on the app background) — only THESE render as
 *  light monochrome marks; colourful emblems (La Liga, Serie A, Euro, WC…)
 *  and all team crests keep their original colours. */
private val LIGHT_EMBLEM_CODES = setOf("CL", "PL", "FL1", "DED")

/**
 * Sticky standings-sheet title: emblem + league name at the left, the
 * next-league chevron at the right. Rendered by the sheet OUTSIDE its scroll
 * container so the name stays pinned while the table scrolls.
 */
@Composable
fun StandingsSheetHeader(
    competition: FootballCompetitionDto?,
    onNextLeague: () -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("flex-shrink", "0")
                property("align-items", "center")
                property("justify-content", "space-between")
                property("padding", "4px 0 10px 0")
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        Row(
            modifier = Modifier.mGap(8),
            verticalAlignment = Alignment.CenterVertically
        ) {
            val emblem = competition?.emblem.orEmpty()
            if (emblem.isNotBlank()) {
                Img(
                    src = emblem,
                    alt = "",
                    attrs = Modifier
                        .mSize(24)
                        .styleModifier {
                            property("object-fit", "contain")
                            property("flex-shrink", "0")
                            leagueEmblemFilter(competition?.code)?.let { property("filter", it) }
                        }
                        .toAttrs()
                )
            }
            SpanText(
                competition?.name.orEmpty(),
                modifier = Modifier
                    .mFontSize(18)
                    .fontWeight(600)
                    .color(com.arena_web.ui.theme.ThemeVars.Text1)
            )
        }
        // Next-league chevron — swaps the table in place (sheet never jumps).
        Box(
            modifier = Modifier
                .mSize(32)
                .mBorderRadius(100)
                .cursor(Cursor.Pointer)
                .onClick { onNextLeague() }
                .styleModifier {
                    property("border", "0.3px solid transparent")
                    property(
                        "background",
                        "${AppGradients.InteractiveSecondary} padding-box, " +
                            "linear-gradient(0deg, var(--l-s2, #0B1628), var(--l-s2, #0B1628)) padding-box, " +
                            "${AppGradients.ButtonBorderGradientPrimary} border-box"
                    )
                },
            contentAlignment = Alignment.Center
        ) {
            Icon(src = com.arena_web.ui.theme.AppIcons.ChevronForward, modifier = Modifier.mSize(12).styleModifier { property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V) })
        }
    }
}

/** Round next-league chevron used by the desktop standings popup. */
@Composable
fun StandingsNextLeagueButton(onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .sSize(34)
            .sBorderRadius(100)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, var(--l-s2, #0B1628), var(--l-s2, #0B1628)) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
            },
        contentAlignment = Alignment.Center
    ) {
        Icon(src = com.arena_web.ui.theme.AppIcons.ChevronForward, modifier = Modifier.sSize(13).styleModifier { property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V) })
    }
}

internal fun leagueEmblemFilter(code: String?): String? =
    if (code != null && code in LIGHT_EMBLEM_CODES) "var(--l-emblem-filter, brightness(0) invert(1) opacity(0.85))" else null

/** Local HH:mm from an ISO UTC kickoff. */
private fun kickoffTime(utcDate: String): String {
    if (utcDate.isBlank()) return ""
    val d = kotlin.js.Date(utcDate)
    val h = d.getHours().toString().padStart(2, '0')
    val m = d.getMinutes().toString().padStart(2, '0')
    return "$h:$m"
}
